import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  TrendingDown, TrendingUp, Shield, Target, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, XCircle, 
  BookOpen, BarChart3, Activity, DollarSign, LineChart
} from 'lucide-react';

const faqData = [
  {
    question: "Short pozisyon ne demek?",
    answer: "Short, bir varligin fiyatinin dusecegi beklentisiyle acilan pozisyondur. Yuksek fiyattan satilir, dusuk fiyattan geri alinir."
  },
  {
    question: "Short islem nasil yapilir?",
    answer: "Futures piyasada satis yonunde pozisyon acilir. Fiyat dustugunde kar, yukseldiginde zarar olusur."
  },
  {
    question: "Short pozisyonda risk nedir?",
    answer: "Fiyat yukselirse zarar olusur. Teorik olarak fiyat sinirsiz yukselecegi icin risk buyuktur. Stop Loss zorunludur."
  },
  {
    question: "Spot piyasada short yapilabilir mi?",
    answer: "Dogrudan hayir. Spot piyasada varlik satilabilir ancak klasik anlamda short pozisyon futures piyasada acilir."
  },
  {
    question: "Short squeeze nedir?",
    answer: "Cok sayida short pozisyon acikken fiyatin ani yukselisi ile short pozisyonlarin zorla kapatilmasidir. Ciddi zarar olusturabilir."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Short Nedir? Kripto Piyasasinda Short Pozisyon Rehberi",
    "description": "Short pozisyon nedir, nasil calisir? Kripto futures islemlerinde short acma mantigi, kaldirac, risk yonetimi ve short squeeze kavrami.",
    "author": { "@type": "Organization", "name": "Borsamex" },
    "publisher": { "@type": "Organization", "name": "Borsamex", "logo": { "@type": "ImageObject", "url": "https://borsamex.com/logo512.png" } },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": "https://borsamex.com/rehber/short-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const ShortNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Short Nedir? Kripto Piyasasinda Short Pozisyon Rehberi | Borsamex"
        description="Short pozisyon nedir, nasil calisir? Kripto futures islemlerinde short acma mantigi, kaldirac, short squeeze ve risk yonetimi. Detayli rehber."
        keywords="short nedir, short pozisyon nedir, short islem nedir, kripto short, short acmak ne demek, short squeeze nedir, bitcoin short"
        canonical="https://borsamex.com/rehber/short-nedir"
        structuredData={structuredData}
      />

      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Short Nedir?</span>
          </div>
        </div>
      </div>

      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-red-500/10 text-red-400 border-red-500/20">Trading</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>8 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Short Nedir? Kripto Piyasasinda Short Pozisyon
            </h1>
            
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-red-400">Short pozisyon</strong>, bir varligin fiyatinin dusecegi beklentisiyle acilan islem yonudur. 
                Trader yuksek fiyattan pozisyon acar ve fiyat dustugunde senaryo olumlu sonuclanir.
              </p>
              <p className="text-slate-400 mt-4">
                Short, ozellikle kripto futures piyasasinda yaygin kullanilan ve dusus trendlerinden de kazanc elde etmeyi amaclayan bir stratejidir.
              </p>
            </div>
          </motion.header>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="prose prose-invert prose-slate max-w-none">
            
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="h-4 w-4 text-red-400" />
                </div>
                Short Pozisyon Ne Demek?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Short (kisa pozisyon), bir varligin degerinin dusecegi beklentisiyle yapilan islemdir. "Yuksekten sat, dusukten geri al" mantigina dayanir.
              </p>
              
              <p className="text-slate-300 mb-4">
                Spot piyasada dogrudan short yapmak mumkun degildir. Short pozisyon genellikle futures (vadeli islem) piyasasinda acilir:
              </p>
              
              <Card className="border-red-500/30 bg-red-950/20 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <LineChart className="h-5 w-5 text-red-400" />
                    <span className="font-bold text-white">Futures Short</span>
                  </div>
                  <p className="text-slate-300 text-sm">Satis yonunde kaldiracli pozisyon acilir. Fiyat dustukce kar artar, yukselirse zarar olusur. Stop Loss ile risk sinirlanir.</p>
                </CardContent>
              </Card>
              
              <p className="text-slate-400">
                Short pozisyon, piyasanin her iki yonunde de firsatlari degerlendirmeyi saglar.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                </div>
                Short Islem Nasil Calisir?
              </h2>
              
              <p className="text-slate-300 mb-4">Short islemin adimlari:</p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mt-0.5">1</Badge>
                  <div>
                    <p className="text-white font-medium">Dusus Sinyali</p>
                    <p className="text-slate-400 text-sm">Teknik gostergeler (RSI asiri alim, MACD bearish crossover, direnc bolgesi) dusus isaret eder</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mt-0.5">2</Badge>
                  <div>
                    <p className="text-white font-medium">Satis Pozisyonu</p>
                    <p className="text-slate-400 text-sm">Futures piyasada satis yonunde pozisyon acilir</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mt-0.5">3</Badge>
                  <div>
                    <p className="text-white font-medium">Fiyat Dususu</p>
                    <p className="text-slate-400 text-sm">Fiyat duserse aradaki fark kar olarak yansir</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mt-0.5">4</Badge>
                  <div>
                    <p className="text-white font-medium">Pozisyon Kapatma</p>
                    <p className="text-slate-400 text-sm">Hedef fiyata ulastiginda (TP) veya risk sinirinda (Stop) pozisyon kapatilir</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Inline AI Sinyal Kutusu */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Ornegi</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">ETH-USDT</span>
                      <Badge className="bg-red-500/20 text-red-400">SHORT</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Giris: $3,820 | TP1: $3,650 | TP2: $3,480 | Stop: $3,950
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Bu analizleri manuel yapmak yerine AI destekli sinyalleri inceleyebilirsin</span>
                    </div>
                  </div>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Sinyalleri Gor <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                </div>
                Short Squeeze Nedir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Short squeeze, cok sayida short pozisyon acikken fiyatin ani ve sert yukselisidir. Bu durumda:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Short pozisyonlar zorla kapatilir (likidasyon)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Kapatma islemi fiyati daha da yukari iter</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Zincirleme etki ile buyuk kayiplar yasanabilir</span>
                </li>
              </ul>
              
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    Short squeeze riski nedeniyle <strong className="text-white">stop loss kullanmak kritiktir</strong>. Kaldiracli short pozisyonlarda bu risk daha buyuktur.
                  </p>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-400" />
                </div>
                Short Pozisyonda Risk Yonetimi
              </h2>
              
              <p className="text-slate-300 mb-4">
                Short pozisyon, long pozisyondan teorik olarak daha risklidir. Cunku fiyat sinirsiz yukselme potansiyeline sahiptir.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Stop Loss zorunlu</strong> - Short pozisyonda stop loss olmadan islem yapmak cok tehlikelidir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Dusuk kaldirac</strong> - Short pozisyonda yuksek kaldirac likidasyon riskini arttirir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Piyasa yonunu dogrula</strong> - Birden fazla gosterge ayni yonu isaret etmeli</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-purple-400" />
                </div>
                Short Pozisyon Ne Zaman Acilir?
              </h2>
              
              <p className="text-slate-300 mb-4">Short pozisyon icin yaygin sinyaller:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">RSI asiri alim bolgesi</strong> - RSI 70 uzerinde iken potansiyel donus sinyali</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">MACD bearish crossover</strong> - Sinyal cizgisi asagi kesisimi</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Direnc bolgesi</strong> - Fiyat guclu direnc seviyesinden donus yaptiginda</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Bearish divergence</strong> - Fiyat yeni zirve yaparken gostergeler yapamadiginda</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Bu gostergeler AI trading sistemlerinde otomatik olarak analiz edilir ve short sinyal uretiminde kullanilir.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex'te Short Sinyaller
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex AI sistemi short sinyallerinde:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Dusus potansiyelini 200+ teknik gosterge ile analiz eder</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Stop Loss seviyesini otomatik hesaplar (short squeeze koruması)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Kademeli TP hedefleri belirler</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-amber-400 mb-2">Onemli Uyari</h3>
                      <p className="text-slate-300">
                        Short pozisyon acmak yatirim tavsiyesi degildir. Short islemler long islemlerden daha yuksek risk icerir. 
                        Kaldiracli short pozisyonlarda sermayenin tamami kaybedilebilir.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                Ilgili Makaleler
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/long-nedir" className="group">
                  <Card className="border-green-500/20 bg-slate-900/50 hover:border-green-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-green-400 transition-colors">Long Nedir?</p>
                          <p className="text-xs text-slate-500">Yukselis yonlu pozisyonlar</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/futures-trading-nedir" className="group">
                  <Card className="border-purple-500/20 bg-slate-900/50 hover:border-purple-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <LineChart className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Futures Trading Nedir?</p>
                          <p className="text-xs text-slate-500">Vadeli islemler rehberi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/macd-nedir" className="group">
                  <Card className="border-blue-500/20 bg-slate-900/50 hover:border-blue-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-blue-400 transition-colors">MACD Nedir?</p>
                          <p className="text-xs text-slate-500">Trend degisim gostergesi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/destek-direnc-nedir" className="group">
                  <Card className="border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-amber-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-amber-400 transition-colors">Destek ve Direnc Nedir?</p>
                          <p className="text-xs text-slate-500">Kritik seviyeler</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sik Sorulan Sorular</h2>
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="Short Nedir? Kripto Short Pozisyon Rehberi"
              url="/rehber/short-nedir"
              hashtags="#kripto #short #bitcoin #trading"
            />

          </motion.div>

          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/long-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Long Nedir?
            </Link>
            <Link to="/rehber/futures-trading-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: Futures Trading Nedir?
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-red-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Short Pozisyon Mantigini Ogrendin
            </h3>
            <p className="text-slate-400 mb-6">
              Simdi canli short sinyallerini takip et ve AI destekli analizleri incele.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ucretsiz Basla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default ShortNedirPage;
